// One navmesh tile built in stages (phase C, gate C5): Recast's tile pipeline (recast-navigation-js's
// `generateTileNavMeshData`, step for step) cut into small units of work, so a rebuild can be spread over many frames
// and the tile in use stays in the mesh until the new one is swapped in by the last unit. The stages: gather the
// triangles that overlap the tile and its border (in batches), make the voxel heightfield, rasterise (in batches),
// filter, compact, erode, the distance field, regions, contours, the polygon mesh, the detail mesh, Detour's tile
// data, and the swap. Each unit's work is bounded by the tile's size (voxels) or a batch size (triangles); the units
// run in the same order whatever the slicing, so a tile built in one call and one built over 20 frames are the same.
import {
  Detour,
  NavMeshCreateParams,
  Raw,
  Recast,
  RecastBuildContext,
  TriangleAreasArray,
  TrianglesArray,
  VerticesArray,
  allocCompactHeightfield,
  allocContourSet,
  allocHeightfield,
  allocPolyMesh,
  allocPolyMeshDetail,
  buildCompactHeightfield,
  buildContours,
  buildDistanceField,
  buildPolyMesh,
  buildPolyMeshDetail,
  buildRegions,
  createHeightfield,
  createNavMeshData,
  erodeWalkableArea,
  filterLedgeSpans,
  filterLowHangingWalkableObstacles,
  filterWalkableLowHeightSpans,
  freeCompactHeightfield,
  freeContourSet,
  freeHeightfield,
  freePolyMesh,
  freePolyMeshDetail,
  markWalkableTriangles,
  rasterizeTriangles,
  statusFailed,
  type NavMesh,
  type RecastCompactHeightfield,
  type RecastContourSet,
  type RecastHeightfield,
  type RecastPolyMesh,
  type RecastPolyMeshDetail,
  type UnsignedCharArray,
  type FloatArray,
  type IntArray,
} from "recast-navigation";
import type { NavGeometry } from "./geometry";
import type { TileXY } from "./navmesh";

/** Recast's numbers for every tile, in voxels (read once from the tiled config). */
export interface TileRecastConfig {
  /** Voxels across a tile with its border on both sides. */
  width: number;
  height: number;
  borderSize: number;
  cs: number;
  ch: number;
  walkableSlopeAngle: number;
  walkableHeight: number;
  walkableClimb: number;
  walkableRadius: number;
  minRegionArea: number;
  mergeRegionArea: number;
  maxSimplificationError: number;
  maxEdgeLen: number;
  maxVertsPerPoly: number;
  detailSampleDist: number;
  detailSampleMaxError: number;
}

/** Batch sizes: triangles gathered and rasterised per unit. */
export const TILE_BATCH = { gather: 512, raster: 1024 };

/** The source triangles of a tile: index lists into one or more geometries. */
export interface TileSource {
  geometry: NavGeometry;
  /** Triangle indices (into `geometry.indices / 3`) that overlap the tile's expanded box. */
  tris: ArrayLike<number>;
}

/** How a tile job ended. */
export type TileOutcome = "built" | "empty" | "failed";

/** The stages, in order (a unit is one stage, or one batch of a batched stage). */
export const TILE_STAGES = ["gather", "heightfield", "raster", "filter", "compact", "erode", "distance", "regions", "contours", "polymesh", "detail", "data", "swap"] as const;
export type TileStage = (typeof TILE_STAGES)[number];

/** A tile being built, one unit at a time. */
export interface TileJob {
  readonly tile: TileXY;
  /** Units done so far. */
  readonly done: number;
  /** The stage the next unit runs. */
  readonly stage: TileStage | "finished";
  /** The outcome once finished, else null. */
  readonly outcome: TileOutcome | null;
  /** Run the next unit; returns true when the job has finished (swapped, empty or failed). */
  unit(): boolean;
  /** Run every remaining unit. */
  finish(): TileOutcome;
  /** Free what the job holds without swapping (a job abandoned for a newer one). */
  cancel(): void;
}

/** The tile box Recast builds (without its border) and with its border (the geometry it needs). */
export function tileBoxes(origin: [number, number, number], top: number, tcs: number, border: number, x: number, y: number) {
  const bmin: [number, number, number] = [origin[0] + x * tcs, origin[1], origin[2] + y * tcs];
  const bmax: [number, number, number] = [origin[0] + (x + 1) * tcs, top, origin[2] + (y + 1) * tcs];
  return { bmin, bmax, emin: [bmin[0] - border, bmin[1], bmin[2] - border] as [number, number, number], emax: [bmax[0] + border, bmax[1], bmax[2] + border] as [number, number, number] };
}

/**
 * A staged tile build. `sources` are the triangles to rasterise (already narrowed to the tile's expanded box, or not:
 * Recast clips to the box). `swap` puts the finished data in the mesh (or removes the tile when it came out empty).
 */
export function createTileJob(o: {
  tile: TileXY;
  navMesh: NavMesh;
  cfg: TileRecastConfig;
  box: { emin: [number, number, number]; emax: [number, number, number] };
  sources: readonly TileSource[];
  ctx: RecastBuildContext;
  buildBvTree?: boolean;
}): TileJob {
  const { tile, navMesh, cfg, box, sources, ctx } = o;
  // the gather's output: one local triangle soup (vertices remapped)
  // sized for the worst case up front (no growing arrays mid-slice)
  const maxTris = sources.reduce((n, s) => n + s.tris.length, 0);
  const pos = new Float32Array(maxTris * 9);
  const idx = new Int32Array(maxTris * 3);
  let nv = 0;
  let ni = 0;
  const remaps: Map<number, number>[] = [];
  let gatherSrc = 0;
  let gatherAt = 0;
  let rasterAt = 0;
  let verts: FloatArray | null = null;
  let tris: IntArray | null = null;
  let hf: RecastHeightfield | null = null;
  let chf: RecastCompactHeightfield | null = null;
  let cset: RecastContourSet | null = null;
  let pmesh: RecastPolyMesh | null = null;
  let dmesh: RecastPolyMeshDetail | null = null;
  let data: UnsignedCharArray | null = null;
  let stage: TileStage | "finished" = "gather";
  let outcome: TileOutcome | null = null;
  let done = 0;

  const free = (): void => {
    verts?.destroy();
    tris?.destroy();
    verts = tris = null;
    if (hf) freeHeightfield(hf);
    if (chf) freeCompactHeightfield(chf);
    if (cset) freeContourSet(cset);
    if (pmesh) freePolyMesh(pmesh);
    if (dmesh) freePolyMeshDetail(dmesh);
    hf = chf = null;
    cset = null;
    pmesh = null;
    dmesh = null;
  };
  const end = (r: TileOutcome): boolean => {
    if (r !== "built") {
      // an empty or failed tile leaves no tile behind: the old one goes too
      const ref = navMesh.getTileRefAt(tile.x, tile.y, 0);
      if (ref) navMesh.removeTile(ref);
    }
    if (data && r !== "built") data.destroy();
    data = null;
    free();
    outcome = r;
    stage = "finished";
    return true;
  };
  const next = (s: TileStage): boolean => {
    stage = s;
    return false;
  };

  const unit = (): boolean => {
    if (stage === "finished") return true;
    done++;
    switch (stage) {
      case "gather": {
        // a batch of source triangles into the local soup; vertices shared within a source keep one copy
        let left = TILE_BATCH.gather;
        while (left > 0 && gatherSrc < sources.length) {
          const s = sources[gatherSrc]!;
          const P = s.geometry.positions;
          const I = s.geometry.indices;
          const remap = (remaps[gatherSrc] ??= new Map());
          for (; gatherAt < s.tris.length && left > 0; gatherAt++, left--) {
            const t = s.tris[gatherAt]!;
            for (let k = 0; k < 3; k++) {
              const v = I[t * 3 + k]!;
              let li = remap.get(v);
              if (li === undefined) {
                li = nv++;
                pos[li * 3] = P[v * 3]!;
                pos[li * 3 + 1] = P[v * 3 + 1]!;
                pos[li * 3 + 2] = P[v * 3 + 2]!;
                remap.set(v, li);
              }
              idx[ni++] = li;
            }
          }
          if (gatherAt >= s.tris.length) {
            gatherSrc++;
            gatherAt = 0;
          }
        }
        if (gatherSrc < sources.length) return false;
        return ni === 0 ? end("empty") : next("heightfield");
      }
      case "heightfield": {
        remaps.length = 0;
        verts = new VerticesArray();
        verts.copy(pos.subarray(0, nv * 3));
        tris = new TrianglesArray();
        tris.copy(idx.subarray(0, ni));
        hf = allocHeightfield();
        if (!createHeightfield(ctx, hf, cfg.width, cfg.height, box.emin, box.emax, cfg.cs, cfg.ch)) return end("failed");
        return next("raster");
      }
      case "raster": {
        const nt = ni / 3;
        const n = Math.min(TILE_BATCH.raster, nt - rasterAt);
        const batch = new TrianglesArray();
        batch.copy(idx.subarray(rasterAt * 3, (rasterAt + n) * 3));
        const areas = new TriangleAreasArray();
        areas.resize(n);
        markWalkableTriangles(ctx, cfg.walkableSlopeAngle, verts!, nv, batch, n, areas);
        const ok = rasterizeTriangles(ctx, verts!, nv, batch, areas, n, hf!, cfg.walkableClimb);
        areas.destroy();
        batch.destroy();
        if (!ok) return end("failed");
        rasterAt += n;
        return rasterAt >= nt ? next("filter") : false;
      }
      case "filter":
        filterLowHangingWalkableObstacles(ctx, cfg.walkableClimb, hf!);
        filterLedgeSpans(ctx, cfg.walkableHeight, cfg.walkableClimb, hf!);
        filterWalkableLowHeightSpans(ctx, cfg.walkableHeight, hf!);
        return next("compact");
      case "compact": {
        chf = allocCompactHeightfield();
        if (!buildCompactHeightfield(ctx, cfg.walkableHeight, cfg.walkableClimb, hf!, chf)) return end("failed");
        freeHeightfield(hf!);
        hf = null;
        return next("erode");
      }
      case "erode":
        return erodeWalkableArea(ctx, cfg.walkableRadius, chf!) ? next("distance") : end("failed");
      case "distance":
        return buildDistanceField(ctx, chf!) ? next("regions") : end("failed");
      case "regions":
        return buildRegions(ctx, chf!, cfg.borderSize, cfg.minRegionArea, cfg.mergeRegionArea) ? next("contours") : end("failed");
      case "contours":
        cset = allocContourSet();
        return buildContours(ctx, chf!, cfg.maxSimplificationError, cfg.maxEdgeLen, cset, Recast.RC_CONTOUR_TESS_WALL_EDGES) ? next("polymesh") : end("failed");
      case "polymesh":
        pmesh = allocPolyMesh();
        return buildPolyMesh(ctx, cset!, cfg.maxVertsPerPoly, pmesh) ? next("detail") : end("failed");
      case "detail": {
        dmesh = allocPolyMeshDetail();
        if (!buildPolyMeshDetail(ctx, pmesh!, chf!, cfg.detailSampleDist, cfg.detailSampleMaxError, dmesh)) return end("failed");
        freeCompactHeightfield(chf!);
        chf = null;
        freeContourSet(cset!);
        cset = null;
        return next("data");
      }
      case "data": {
        const pm = pmesh!;
        if (pm.npolys() === 0) return end("empty");
        // the polygons' flags from their areas, as recast-navigation-js's generator sets them
        for (let i = 0; i < pm.npolys(); i++) {
          if (pm.areas(i) === Recast.RC_WALKABLE_AREA) pm.setAreas(i, 0);
          if (pm.areas(i) === 0) pm.setFlags(i, 1);
        }
        const params = new NavMeshCreateParams();
        params.setPolyMeshCreateParams(pm);
        params.setPolyMeshDetailCreateParams(dmesh!);
        params.setWalkableHeight(cfg.walkableHeight * cfg.ch);
        params.setWalkableRadius(cfg.walkableRadius * cfg.cs);
        params.setWalkableClimb(cfg.walkableClimb * cfg.ch);
        params.setCellSize(cfg.cs);
        params.setCellHeight(cfg.ch);
        params.setBuildBvTree(o.buildBvTree ?? true);
        params.setTileX(tile.x);
        params.setTileY(tile.y);
        const r = createNavMeshData(params);
        Raw.destroy(params.raw);
        if (!r.success) return end("failed");
        data = r.navMeshData;
        return next("swap");
      }
      case "swap": {
        const ref = navMesh.getTileRefAt(tile.x, tile.y, 0);
        if (ref) navMesh.removeTile(ref);
        const added = navMesh.addTile(data!, Detour.DT_TILE_FREE_DATA, 0);
        if (statusFailed(added.status)) return end("failed");
        data = null; // Detour owns it now
        return end("built");
      }
    }
  };

  return {
    tile,
    get done() {
      return done;
    },
    get stage() {
      return stage;
    },
    get outcome() {
      return outcome;
    },
    unit,
    finish() {
      while (!unit());
      return outcome!;
    },
    cancel() {
      data?.destroy();
      data = null;
      free();
      stage = "finished";
      outcome = outcome ?? "failed";
    },
  };
}

/** Read the per-tile Recast numbers from the tiled config recast-navigation-js builds. */
export function tileRecastConfig(rc: { width: number; height: number; borderSize: number; cs: number; ch: number; walkableSlopeAngle: number; walkableHeight: number; walkableClimb: number; walkableRadius: number; minRegionArea: number; mergeRegionArea: number; maxSimplificationError: number; maxEdgeLen: number; maxVertsPerPoly: number; detailSampleDist: number; detailSampleMaxError: number }): TileRecastConfig {
  return {
    width: rc.width,
    height: rc.height,
    borderSize: rc.borderSize,
    cs: rc.cs,
    ch: rc.ch,
    walkableSlopeAngle: rc.walkableSlopeAngle,
    walkableHeight: rc.walkableHeight,
    walkableClimb: rc.walkableClimb,
    walkableRadius: rc.walkableRadius,
    minRegionArea: rc.minRegionArea,
    mergeRegionArea: rc.mergeRegionArea,
    maxSimplificationError: rc.maxSimplificationError,
    maxEdgeLen: rc.maxEdgeLen,
    maxVertsPerPoly: rc.maxVertsPerPoly,
    detailSampleDist: rc.detailSampleDist,
    detailSampleMaxError: rc.detailSampleMaxError,
  };
}

/** A shared build context with Recast's logs and timers off (they cost time in every stage). */
export const quietContext = (): RecastBuildContext => new RecastBuildContext(false);
